import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { formatCurrency } from '../../../utils/currency';
import { getCurrentCompanyName, getCurrentCompanyAddress, getCurrentCompanyLogo, getCurrentCompanyId } from '../../../utils/auth';
import { downloadInvoicePDF } from '../../../utils/pdfGenerator';
import { customersAPI } from '../../../lib/api';

const InvoicePreviewModal = ({ isOpen, onClose, invoice }) => {
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  // Fetch customer data when invoice changes
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!invoice?.customerId) return;
      
      try {
        setLoadingCustomer(true);
        const companyId = getCurrentCompanyId();
        const response = await customersAPI.getAll(companyId);
        
        let customers = [];
        if (response.data?.Success && Array.isArray(response.data.Data)) {
          customers = response.data.Data;
        } else if (Array.isArray(response.data)) {
          customers = response.data;
        }
        
        const customer = customers.find(c => 
          (c.Id || c.id) === invoice.customerId
        );
        
        if (customer) {
          setCustomerData({
            companyName: customer.CompanyName || customer.companyName,
            email: customer.Email || customer.email,
            address: customer.Address || customer.address,
            city: customer.City || customer.city,
            state: customer.State || customer.state,
            zipCode: customer.ZipCode || customer.zipCode,
            country: customer.Country || customer.country,
            phone: customer.Phone || customer.phone
          });
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
      } finally {
        setLoadingCustomer(false);
      }
    };

    if (isOpen && invoice) {
      fetchCustomerData();
    }
  }, [isOpen, invoice]);

  if (!isOpen || !invoice) return null;

  // Get company information from login data
  const companyName = getCurrentCompanyName();
  const companyAddress = getCurrentCompanyAddress();
  const companyLogo = getCurrentCompanyLogo();
  const documentType = invoice?.documentType || invoice?.DocumentType || 'Invoice';

  const formatAmount = (amount) => {
    return formatCurrency(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDownloadPDF = async () => {
    const result = await downloadInvoicePDF('invoice-content', invoice?.invoiceNumber || 'invoice');
    if (!result.success) {
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">{documentType} Preview</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              iconName="Download"
              iconPosition="left"
            >
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
            >
              <span className="hidden sm:inline">Print</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              aria-label="Close preview"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-muted/20">
          <div id="invoice-content" className="bg-background rounded-lg shadow-sm border border-border p-8 max-w-3xl mx-auto" style={{ minHeight: 'auto' }}>
            {/* Header with blue and yellow Esque colors */}
            <div className="flex items-start justify-between mb-8 pb-6 border-b-4" style={{ borderColor: '#2563EB' }}>
              <div className="flex items-center gap-4">
                {companyLogo ? (
                  <img 
                    src={companyLogo} 
                    alt={companyName}
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-md flex items-center justify-center" style={{ backgroundColor: '#2563EB' }}>
                    <Icon name="Building2" size={32} color="#FCD34D" />
                  </div>
                )}
                <div>
                  <h1 className="font-heading font-bold text-2xl" style={{ color: '#2563EB' }}>{companyName}</h1>
                </div>
              </div>

              <div className="text-right">
                <h2 className="font-heading font-bold text-3xl mb-2" style={{ color: '#FCD34D', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
                  {documentType === 'Proforma' ? 'PROFORMA INVOICE' : documentType.toUpperCase()}
                </h2>
                <div className="space-y-1">
                  <p className="data-text font-semibold text-foreground">{invoice?.invoiceNumber}</p>
                  <InvoiceStatusBadge status={invoice?.status} />
                </div>
              </div>
            </div>

            {/* Company Address */}
            <div className="mb-6">
              <div className="caption text-muted-foreground space-y-1">
                {companyAddress ? (
                  <>
                    {companyAddress.street && <p>{companyAddress.street}</p>}
                    {(companyAddress.city || companyAddress.state || companyAddress.zipCode) && (
                      <p>
                        {[companyAddress.city, companyAddress.state, companyAddress.zipCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                    {companyAddress.country && <p>{companyAddress.country}</p>}
                    {companyAddress.email && <p>{companyAddress.email}</p>}
                    {companyAddress.phone && <p>{companyAddress.phone}</p>}
                  </>
                ) : (
                  <>
                    <p>Company address not available</p>
                    <p>Please update company information</p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-border">
              <div>
                <h3 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>BILL TO</h3>
                <div className="space-y-1">
                  <p className="font-body font-semibold text-foreground">{invoice?.customerName || invoice?.customer}</p>
                  {customerData ? (
                    <>
                      {customerData.address && <p className="caption text-muted-foreground">{customerData.address}</p>}
                      {(customerData.city || customerData.state || customerData.zipCode) && (
                        <p className="caption text-muted-foreground">
                          {[customerData.city, customerData.state, customerData.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                      {customerData.country && <p className="caption text-muted-foreground">{customerData.country}</p>}
                    </>
                  ) : loadingCustomer ? (
                    <p className="caption text-muted-foreground">Loading address...</p>
                  ) : null}
                  {invoice?.customerEmail && <p className="caption text-muted-foreground">{invoice.customerEmail}</p>}
                </div>
              </div>

              <div className="md:text-right">
                <div className="space-y-2">
                  <div>
                    <span className="caption text-muted-foreground">Issue Date: </span>
                    <span className="font-body font-medium text-foreground">{formatDate(invoice?.issueDate)}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground">{documentType === 'Quote' ? 'Valid Until: ' : 'Due Date: '}</span>
                    <span className="font-body font-medium text-foreground">{formatDate(invoice?.dueDate)}</span>
                  </div>
                  {invoice?.paidAmount > 0 && (
                    <div>
                      <span className="caption text-muted-foreground">Amount Paid: </span>
                      <span className="font-body font-medium text-success">{formatAmount(invoice?.paidAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#2563EB' }}>
                    <th className="text-left py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>DESCRIPTION</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>QTY</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>RATE</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice?.lineItems && invoice.lineItems.length > 0 ? (
                    invoice.lineItems.map((item, index) => (
                      <tr key={index} className="border-b border-border">
                        <td className="py-4 font-body text-sm text-foreground">{item.description}</td>
                        <td className="py-4 text-right data-text text-foreground">{item.quantity}</td>
                        <td className="py-4 text-right data-text text-foreground">{formatAmount(item.rate)}</td>
                        <td className="py-4 text-right data-text font-semibold text-foreground">{formatAmount(item.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b border-border">
                      <td className="py-4 font-body text-sm text-muted-foreground italic" colSpan="4">
                        No line items available for this invoice
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-8">
              <div className="w-full md:w-80 space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="font-body text-sm text-muted-foreground">Subtotal</span>
                  <span className="data-text font-semibold text-foreground">{formatAmount(invoice?.subtotal || invoice?.amount)}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="font-body text-sm text-muted-foreground">VAT ({invoice?.vatRate || 16}%)</span>
                  <span className="data-text font-semibold text-foreground">{formatAmount(invoice?.taxAmount || 0)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-t-2" style={{ borderColor: '#2563EB' }}>
                  <span className="font-heading font-bold text-base" style={{ color: '#2563EB' }}>Total</span>
                  <span className="data-text font-bold text-2xl" style={{ color: '#FCD34D', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>{formatAmount(invoice?.amount)}</span>
                </div>
                {invoice?.paidAmount > 0 && (
                  <div className="flex items-center justify-between py-2 bg-success/10 px-3 rounded-md">
                    <span className="font-body font-medium text-sm text-success">Amount Due</span>
                    <span className="data-text font-bold text-lg text-success">
                      {formatAmount(invoice?.amount - invoice?.paidAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              {invoice?.notes && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Payment Terms</h4>
                  <p className="caption text-muted-foreground whitespace-pre-line">
                    {invoice.notes}
                  </p>
                </div>
              )}

              {invoice?.terms && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Terms & Conditions</h4>
                  <p className="caption text-muted-foreground whitespace-pre-line">
                    {invoice.terms}
                  </p>
                </div>
              )}

              {!invoice?.notes && !invoice?.terms && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Notes</h4>
                  <p className="caption text-muted-foreground">
                    Thank you for your business. If you have any questions about this invoice, please contact us.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            variant="default"
            onClick={handleDownloadPDF}
            iconName="Download"
            iconPosition="left"
          >
            Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewModal;